"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { Input, Switch, toast } from "@instacover/ui-web"
import { useTranslations } from "next-intl"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { resendMfaCode, signin, verifyMfaCode } from "@/app/[locale]/auth/actions"
import { Heading } from "@/components/Heading"
import { PasswordInput } from "@/components/PasswordInput"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormSubmitButton,
  FormWrapper,
} from "@/components/shadcn/Form"
import { Spacer } from "@/components/Spacer"
import { Text } from "@/components/Text"
import { urls } from "@/constants/urls"
import { mxpSignInEvent } from "@/services/mixpanel/mixpanel"
import { Link, useRouter } from "@/utils/navigation"

const formSchema = z.object({
  email: z.email(),
  password: z.string(),
})

const mfaFormSchema = z.object({
  code: z.string().min(1),
  rememberDevice: z.boolean(),
})

export function SignInForm() {
  const router = useRouter()
  const t = useTranslations("auth")

  // When set, the user has passed the password step and must enter the emailed MFA code.
  const [mfaEmail, setMfaEmail] = useState<string | null>(null)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: "", password: "" },
  })

  const mfaForm = useForm<z.infer<typeof mfaFormSchema>>({
    resolver: zodResolver(mfaFormSchema),
    defaultValues: { code: "", rememberDevice: false },
  })

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const res = await signin({ email: values.email, password: values.password })

    if (res?.data?.error) {
      toast.error(res.data.error)
      return
    }

    if (res?.serverError || res?.validationErrors) {
      toast.error("Something went wrong.", {
        description: "Please try again later. If the problem persists, please let us know.",
      })

      return
    }

    if (!res?.data) {
      return
    }

    if (res.data.mfaRequired) {
      setMfaEmail(res.data.email)
      return
    }

    const { user } = res.data

    if (user) {
      mxpSignInEvent(user)
    }

    router.push(urls.app.home)
  }

  async function onMfaSubmit(values: z.infer<typeof mfaFormSchema>) {
    if (!mfaEmail) {
      return
    }

    const res = await verifyMfaCode({
      email: mfaEmail,
      code: values.code,
      rememberDevice: values.rememberDevice,
    })

    if (res?.data?.error) {
      toast.error(t(res.data.error))
      return
    }

    if (res?.serverError || res?.validationErrors) {
      toast.error("Something went wrong.", {
        description: "Please try again later. If the problem persists, please let us know.",
      })

      return
    }

    const user = res?.data?.user

    if (user) {
      mxpSignInEvent(user)
    }

    router.push(urls.app.home)
  }

  async function onResend() {
    if (!mfaEmail) {
      return
    }

    await resendMfaCode({ email: mfaEmail })
    toast.success(t("mfaResent"))
  }

  if (mfaEmail) {
    return (
      <>
        <Heading order={1} size={2} className="text-center">
          {t("mfaHeading")}
        </Heading>

        <Spacer className="h-4" />

        <Text size="sm" className="text-center">
          {t("mfaDescription")}
        </Text>

        <Spacer className="h-10" />

        <FormWrapper {...mfaForm}>
          <form onSubmit={mfaForm.handleSubmit(onMfaSubmit)}>
            <FormField
              name="code"
              control={mfaForm.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("mfaCodeLabel")}</FormLabel>

                  <FormControl>
                    <Input
                      {...field}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="••••••"
                    />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />

            <Spacer className="h-4" />

            <FormField
              name="rememberDevice"
              control={mfaForm.control}
              render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel>
                    <Text size="sm">{t("mfaRememberDevice")}</Text>
                  </FormLabel>

                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <Spacer className="h-10" />

            <FormSubmitButton className="w-full" size="sm">
              {t("mfaSubmitButton")}
            </FormSubmitButton>
          </form>
        </FormWrapper>

        <Spacer className="h-4" />

        <div className="flex justify-center">
          <button type="button" onClick={onResend}>
            <Text variant="link" size="sm">
              {t("mfaResend")}
            </Text>
          </button>
        </div>
      </>
    )
  }

  return (
    <>
      <Heading order={1} size={2} className="text-center">
        {t("signInHeading")}
      </Heading>

      <Spacer className="h-10" />

      <FormWrapper {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("emailLabel")}</FormLabel>

                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    autoComplete="email"
                    placeholder={t("emailPlaceholder")}
                    data-testid="signin-email-input"
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <Spacer className="h-4" />

          <FormField
            name="password"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  <Text size="sm">{t("signInPasswordLabel")}</Text>
                </FormLabel>

                <FormControl>
                  <PasswordInput
                    {...field}
                    placeholder="password"
                    autoComplete="current-password"
                    data-testid="signin-password-input"
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <Spacer className="h-10" />

          <FormSubmitButton className="w-full" size="sm" data-testid="signin-submit-btn">
            {t("signInSubmitButton")}
          </FormSubmitButton>
        </form>
      </FormWrapper>

      <Spacer className="h-4" />

      <div className="flex justify-center gap-4">
        <Link href={urls.auth.signup}>
          <Text variant="link" size="sm">
            {t("signInFirstLogin")}
          </Text>
        </Link>

        <Link href={urls.auth.resetPassword}>
          <Text variant="link" size="sm">
            {t("signInForgotPasswordLink")}
          </Text>
        </Link>
      </div>
    </>
  )
}
